import React, { useState, useEffect, useRef, useMemo } from 'react';
import CommonModal from '../../components/commonComponent/CommonModal';
import CommonTable from '../../components/commonComponent/CommanTable';
import { FaEllipsisV, FaPlus, FaDownload, FaPaperclip } from 'react-icons/fa';
import axios from '../../utils/axiosInstance';
import { toastSuccess, toastError, confirmDelete } from '../../utils/alertHelper';

// Helper for dot-style badge
const DotBadge = ({ color, text, bg }) => (
  <span
    style={{
      background: bg || '#F2F4F7',
      color,
      fontWeight: 500,
      borderRadius: 16,
      fontSize: 14,  
      display: 'inline-flex',
      alignItems: 'center',
      padding: '2px 12px',
      gap: 6,
    }}
  >
    <span style={{
      display: 'inline-block',
      width: 8,
      height: 8,
      borderRadius: '100%',
      marginRight: 6,
      background: color,
    }} />
    {text}
  </span>
);

// Status badge color mapping
const statusColors = {
  'New':    { dot: '#B33BFC', bg: '#F4E2FF', text: '#B33BFC' },
  'In Progress': { dot: '#F59E0B', bg: '#FEF1DB', text: '#F59E0B' },
  'Resolved':   { dot: '#22C55E', bg: '#DEF7E7', text: '#22C55E' },
  'Not Possible': { dot: '#E14B4B', bg: '#FBE4E4', text: '#E14B4B' },
  'Low':    { dot: '#3C64FF', bg: '#E2E8FF', text: '#3C64FF' },
  'Medium': { dot: '#AEAE08', bg: '#F3F3DA', text: '#AEAE08' },
  'High':   { dot: '#E14B4B', bg: '#FBE4E4', text: '#E14B4B' },
};

// Table columns
const ticketFields = [
  { key: 'srNo', label: 'Sr.no' },
  { key: 'ticketNo', label: 'Ticket No.' },
  { key: 'subject', label: 'Ticket Subject' },
  { key: 'requesterName', label: 'Requester Name' },
  { key: 'requestedDate', label: 'Requested Date' },
  { key: 'priority', label: 'Priority' },
  { key: 'requirement', label: 'Requirement' },
  { key: 'status', label: 'Status' },
  { key: 'actions', label: 'Action' },
];

const initialTicketValues = {
  id: "",
  subject: "",
  user: "",
  description: "",
  file: null,
  priority: "Low",
  status: "New",
};

function SupportTicket() {
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState(initialTicketValues);
  const [fileName, setFileName] = useState("");
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  
  const [showFilters, setShowFilters] = useState(false);
  const [userOptions, setUserOptions] = useState([]);
  const [showColumnDropdown, setShowColumnDropdown] = useState(false);
  const columnDropdownRef = useRef(null);
  const forcedFields = ['srNo'];
  const [isExporting, setIsExporting] = useState(false);
  const defaultLimit = process.env.REACT_APP_PAGE_LIMIT ? parseInt(process.env.REACT_APP_PAGE_LIMIT, 10) : 10;
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(defaultLimit);

  // Static status options instead of fetching from API
  const statusOptions = [
    { value: 'New', label: 'New' },
    { value: 'In Progress', label: 'In Progress' },
    { value: 'Not Possible', label: 'Not Possible' }
  ];

  // Localstorage for visible columns
  const getInitialVisibleFields = () => {
    const stored = localStorage.getItem('_visible_ticket_columns');
    return stored ? JSON.parse(stored) : ticketFields.map(f => f.key);
  };
  const [visibleFields, setVisibleFields] = useState(getInitialVisibleFields);

  useEffect(() => {
    localStorage.setItem('_visible_ticket_columns', JSON.stringify(visibleFields));
  }, [visibleFields]);

  useEffect(() => {
    if (!showColumnDropdown) return;
    function handleClickOutside(event) {
      if (columnDropdownRef.current && !columnDropdownRef.current.contains(event.target)) {
        setShowColumnDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showColumnDropdown]);

  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
  });

  // --- Fetch API Data ---
  useEffect(() => {
    async function fetchTickets() {
      try {
        const response = await axios.get('/admin/api/support-ticket');
        if (response.data && response.data.Status === 1 && Array.isArray(response.data.tickets)) {
          const apiTickets = response.data.tickets.map((t) => ({
            id: t._id,
            ticketNo: t.ticketNumber,
            subject: t.subject,
            requesterName: t.createdBy ? (t.createdBy.firstName + " " + t.createdBy.lastName) : "",
            requestedDate: t.createdAt,
            priority: t.priority,
            requirement: t.description,
            status: t.status,
          }));
          setTickets(apiTickets);
          applyFilters(apiTickets);
        } else {
          setTickets([]);
          applyFilters([]);
        }
      } catch (err) {
        setTickets([]);
        applyFilters([]);
      }
    }
    fetchTickets();
  }, []);

  useEffect(() => {
    async function fetchUsers() {
      try {
        const res = await axios.get('/api/user');
        if (res.data && Array.isArray(res.data.users)) {
          setUserOptions(
            res.data.users.map(u => ({
              value: u._id,
              label: `${u.firstName || ""} ${u.lastName || ""}`.trim()
            }))
          );
        } else {
          setUserOptions([]);
        }
      } catch (err) {
        setUserOptions([]);
      }
    }
    fetchUsers();
  }, []);

  // Filtering/Searching
  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    applyFilters(tickets, newFilters);
  };

  useEffect(() => {
    applyFilters(tickets);
    // eslint-disable-next-line
  }, [filters, tickets]);

  const tableColumns = useMemo(
    () =>
      ticketFields
        .filter(field => forcedFields.includes(field.key) || visibleFields.includes(field.key))
        .map(field => ({ key: field.key, label: field.label })),
    [visibleFields]
  );

  const tableData = useMemo(
    () =>
      filteredTickets.map((ticket, idx) => ({
        srNo: idx + 1,
        ticketNo: ticket.ticketNo,
        subject: ticket.subject,
        requesterName: ticket.requesterName,
        requestedDate: ticket.requestedDate
          ? new Date(ticket.requestedDate).toLocaleString('en-GB', {
              hour: '2-digit',
              minute: '2-digit',
              day: '2-digit',
              month: 'short',
              year: 'numeric',
            })
          : '',
        priority: (
          <DotBadge
            color={statusColors[ticket.priority]?.dot || '#b0b7c3'}
            text={ticket.priority}
            bg={statusColors[ticket.priority]?.bg}
          />
        ),
        requirement: ticket.requirement,
        status: (
          <DotBadge
            color={statusColors[ticket.status]?.dot || '#b0b7c3'}
            text={ticket.status}
            bg={statusColors[ticket.status]?.bg}
          />
        ),
        actions: (
          <div>
            <button
              className="btn btn-sm me-1 py-0 px-1 fs-6 lh-sm shadow-none border-0"
              title="Edit"
              onClick={() => handleEdit(ticket)}
            >
              <i className="fa fa-edit"></i>
            </button>
            <button
              className="btn btn-sm py-0 px-1 fs-6 lh-sm shadow-none border-0 text-danger"
              title="Delete"
              onClick={() => handleDelete(ticket.id)}
            >
              <i className="fa fa-trash"></i>
            </button>
          </div>
        ),
      })),
    [filteredTickets]
  );

  // Modal helpers
  const handleAdd = () => {
    setModalData(initialTicketValues);
    setFileName("");
    setErrors({});
    setShowModal(true);
  };

  const handleEdit = (ticket) => {
    setModalData({
      id: ticket.id,
      subject: ticket.subject,
      user: userOptions.length > 0 ? userOptions[0].value : "",
      description: ticket.requirement,
      file: null,
      priority: ticket.priority || "Low",
      status: ticket.status || "",
    });
    setFileName("");
    setErrors({});
    setShowModal(true);
  };

  const handleDelete = async (ticketId) => {
    try {
      const res = await confirmDelete('Are you sure you want to delete this ticket?');
      if (!res.isConfirmed) return;
    } catch (err) {
      return;
    }
    try {
      const response = await axios.delete(`/admin/api/support-ticket/${ticketId}`);
      if (response.data && response.data.Status === 1) {
        // Remove ticket from local state
        const updatedTickets = tickets.filter(ticket => ticket.id !== ticketId);
        setTickets(updatedTickets);
        applyFilters(updatedTickets);
        toastSuccess('Ticket deleted successfully!');
      } else {
        toastError(response.data.Message || 'Failed to delete ticket');
      }
    } catch (error) {
      console.error('Error deleting ticket:', error);
      toastError(error.response?.data?.Message || 'Failed to delete ticket');
    }
  };

  const handleModalChange = (updated) => {
    setModalData(prev => ({ ...prev, ...updated }));
    if (updated.file) setFileName(updated.file.name);
  };

  // Modal Form fields for CommonModal
  const modalFields = [
    { type: 'heading', label: 'Ticket Details' },
    { name: 'subject', label: 'Ticket Subject', type: 'text', placeholder: 'Ticket Subject' },
    { name: 'user', label: 'Select User', type: 'select', options: userOptions },
    { type: 'heading', label: 'Description Details' },
    {
      name: 'description',
      label: '',
      type: 'textarea', // swap for 'wysiwyg' if you integrate an editor
      placeholder: 'Enter description...',
      rows: 6,
    },
    {
      name: 'file',
      label: (
        <span style={{ color: '#5B8DEF', cursor: 'pointer' }}>
          <FaPaperclip className="me-1" /> Upload File
        </span>
      ),
      type: 'file',
      accept: '.pdf,.jpg,.png,.doc,.docx',
    },
    {
      name: 'priority',
      label: 'Priority',
      type: 'select',
      options: [
        { value: 'Low', label: 'Low' },
        { value: 'Medium', label: 'Medium' },
        { value: 'High', label: 'High' }
      ]
    },
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      options: statusOptions
    },
  ];

  // Modal form submit
  const handleSubmit = async () => {
    const validationErrors = {};
    if (!modalData.subject) validationErrors.subject = "Required";
    if (!modalData.user) validationErrors.user = "Required";
    if (!modalData.description) validationErrors.description = "Required";
    if (!modalData.priority) validationErrors.priority = "Required";
    if (!modalData.status) validationErrors.status = "Required";
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    setIsSubmitting(true);
    try {
      const businessId = localStorage.getItem("businessId") || "";
      const createdFromId = localStorage.getItem("userId") || "";

      const payload = {
        subject: modalData.subject,
        description: modalData.description,
        priority: modalData.priority,
        business: businessId,
        createdFrom: createdFromId,
        status: modalData.status,
      };

      let response;
      if (modalData.id) {
        response = await axios.put(`/admin/api/support-ticket/${modalData.id}`, payload);
      } else {
        response = await axios.post('/admin/api/support-ticket', payload);
      }

      if (response.data && response.data.Status === 1 && response.data.ticket) {
        const t = response.data.ticket;
        const newTicket = {
          id: t._id,
          ticketNo: t.ticketNumber,
          subject: t.subject,
          requesterName: t.createdBy?.firstName + " " + t.createdBy?.lastName,
          requestedDate: t.createdAt,
          priority: t.priority,
          requirement: t.description,
          status: t.status,
        };
        let updatedTickets;
        if (modalData.id) {
          updatedTickets = tickets.map(p => p.id === modalData.id ? newTicket : p);
        } else {
          updatedTickets = [...tickets, newTicket];
        }
        setTickets(updatedTickets);
        applyFilters(updatedTickets);
        setShowModal(false);
        setModalData(initialTicketValues);
        setFileName("");
        setErrors({});
      } else {
        setErrors({ api: response.data.Message || "Failed to save ticket" });
      }
    } catch (error) {
      setErrors({ api: error.response?.data?.Message || "Failed to save ticket" });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper to extract filename from Content-Disposition header
  const getFilenameFromDisposition = (disposition) => {
    let filename = 'support-tickets.xlsx';
    if (!disposition) return filename;
    try {
      // Matches filename*=UTF-8''encoded-name or filename="name"
      const filenameRegex = /filename\*=(?:UTF-8'')?([^;\n]+)|filename=\"?([^\";\n]+)\"?/i;
      const matches = disposition.match(filenameRegex);
      if (matches) {
        const name = matches[1] || matches[2];
        return decodeURIComponent(name.replace(/"/g, ''));
      }
    } catch (e) {
      // ignore and fall back
    }
    return filename;
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const params = {
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
      };

      const response = await axios.get('/admin/api/support-ticket/export-excel', {
        params,
        responseType: 'blob',
      });

      const disposition = response.headers['content-disposition'] || response.headers['Content-Disposition'];
      const filename = getFilenameFromDisposition(disposition);
      const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      toastSuccess('Export started');
    } catch (error) {
      console.error('Export error:', error);
      toastError(error.response?.data?.Message || 'Failed to export tickets');
    } finally {
      setIsExporting(false);
    }
  };

  const handleClearFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
    });
    setFilteredTickets(tickets);
  };

  // Filtering logic
  function applyFilters(dataToFilter = tickets, filtersToApply = filters) {
    let filtered = [...dataToFilter];
    // Only date filters are applied (startDate / endDate)
    if (filtersToApply.startDate) {
      filtered = filtered.filter(ticket =>
        ticket.requestedDate >= filtersToApply.startDate
      );
    }
    if (filtersToApply.endDate) {
      filtered = filtered.filter(ticket =>
        ticket.requestedDate <= filtersToApply.endDate
      );
    }
    setFilteredTickets(filtered);
  }

  return (
    <>
      <div className="container-fluid mt-4">
        <div className="d-flex justify-content-between align-items-center mb-4 mt-4">
          <h2 className="h2 mb-0">Support Ticket</h2>
          <div className="d-flex gap-2 align-items-center">
            <button
              className="btn btn-light"
              onClick={() => setShowFilters(!showFilters)}
            >
              <i className="fas fa-filter me-1"></i> Filter
            </button>
            <button
              className="btn btn-light"
              onClick={handleExport}
              disabled={isExporting}
              title={isExporting ? 'Preparing export...' : 'Export all fields'}
            >
              <FaDownload className="me-1" /> {isExporting ? 'Exporting...' : 'Export'}
            </button>
            <button
              className="btn btn-primary"
              onClick={handleAdd}
            >
              <FaPlus className="me-1" /> Create Ticket
            </button>
            <div style={{ position: 'relative' }} ref={columnDropdownRef}>
              {/* Uncomment to enable column dropdown */}
              {/* <button
                className="btn btn-primary btn-sm"
                onClick={() => setShowColumnDropdown(show => !show)}
              >
                <i className="fas fa-eye me-1"></i>
              </button> */}
              {showColumnDropdown && (
                <div
                  className="dropdown-menu show p-3"
                  style={{
                    maxHeight: 250,
                    overflowY: 'auto',
                    right: 0,
                    left: 'auto',
                    position: 'absolute',
                    zIndex: 1050,
                    width: 'max-content',
                    minWidth: 220,
                  }}
                >
                  {ticketFields.map(col => (
                    <div key={col.key} className="form-check mb-1">
                      <input
                        type="checkbox"
                        id={`col-filter-${col.key}`}
                        className="form-check-input"
                        checked={visibleFields.includes(col.key) || forcedFields.includes(col.key)}
                        disabled={forcedFields.includes(col.key)}
                        onChange={e => {
                          if (e.target.checked) {
                            setVisibleFields(prev => {
                              const allKeys = ticketFields.map(c => c.key);
                              const newCols = [...prev, col.key];
                              const uniqueCols = Array.from(new Set(newCols));
                              uniqueCols.sort((a, b) => allKeys.indexOf(a) - allKeys.indexOf(b));
                              return uniqueCols;
                            });
                          } else {
                            setVisibleFields(prev => prev.filter(k => k !== col.key));
                          }
                        }}
                      />
                      <label htmlFor={`col-filter-${col.key}`} className="form-check-label">
                        {col.label}
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Filters - Card layout (Start/End Date) */}
        {showFilters && (
          <div className="mb-4">
            <div className="card p-4 mb-3">
              <div>
                <div className="row g-3 align-items-end">
                  <div className="col-md-3 col-sm-6">
                    <label className="form-label mb-1">Start Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={filters.startDate}
                      onChange={e => handleFilterChange('startDate', e.target.value)}
                    />
                  </div>
                  <div className="col-md-3 col-sm-6">
                    <label className="form-label mb-1">End Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={filters.endDate}
                      onChange={e => handleFilterChange('endDate', e.target.value)}
                    />
                  </div>
                  <div className="col-md-12 d-flex justify-content-center gap-2">
                    <button
                      className="btn btn-primary"
                      onClick={() => applyFilters(tickets, filters)}
                    >
                      Apply
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={handleClearFilters}
                    >
                      Clear
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Support Ticket Table */}
        <CommonTable
          columns={tableColumns}
          data={tableData}
          itemsPerPageOptions={[10, 15, 25, 50]}
          defaultItemsPerPage={15}
          page={page}
          onPageChange={setPage}
          itemsPerPage={limit}
          onItemsPerPageChange={setLimit}
        />
        {/* Modal Form */}
        <CommonModal
          visible={showModal}
          title={modalData.id ? "Edit Ticket" : "Create Ticket"}
          fields={modalFields}
          values={modalData}
          onChange={handleModalChange}
          onCancel={() => {
            setShowModal(false);
            setModalData(initialTicketValues);
            setFileName("");
            setErrors({});
          }}
          onSubmit={handleSubmit}
          submitLabel={isSubmitting ? "Saving..." : "Save"}
          errors={errors}
          requiredFields={['subject', 'user', 'description', 'priority', 'status']}
          disabled={isSubmitting}
        />
        {errors.api && (
          <div className="alert alert-danger mt-2">{errors.api}</div>
        )}
      </div>
    </>
  );
}

export default SupportTicket;